import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useTranslations } from "@/i18n/hooks";
import { FloatingLanguageSelector } from "@/components/FloatingLanguageSelector";
import { Logger } from "@/lib/utils/logger";

const logger = new Logger('CoachForgotPassword');

export function CoachForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const t = useTranslations('coach');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!email || !email.includes('@')) {
      setError(t('forgotPassword.errorValidEmail'));
      return;
    }
    
    setLoading(true);
    
    try {
      logger.info('Initiating coach password reset request', { email });
      
      // Call the custom Supabase function endpoint directly with authorization
      const response = await fetch('https://xuqkgeusasosxtcfhbwj.supabase.co/functions/v1/reset_password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inh1cWtnZXVzYXNvc3h0Y2ZoYndqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzYzMDg1OTUsImV4cCI6MjA1MTg4NDU5NX0.HF4gYNKUEct3xetUJ3NwB1pxgBGBSE2V6CY20iF1Ivs'
        },
        body: JSON.stringify({ 
          email,
          role: 'specialist' // Specify the role as specialist for coaches
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        logger.error('Failed to send coach reset email', { error: data.error || response.statusText });
        setError(data.error || t('forgotPassword.errorSendingReset'));
        return;
      }
      
      logger.info('Coach reset email sent successfully', { response: data });
      setEmailSent(true);
      
      toast({
        title: t('forgotPassword.resetEmailSent'),
        description: t('forgotPassword.resetEmailSentDesc'),
        variant: "default"
      });
    } catch (err) {
      logger.error('Unexpected error sending coach reset email', { error: err });
      setError(t('forgotPassword.errorUnexpected'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="flex justify-between items-start">
        <Link to="/coach-signin" className="inline-flex items-center text-gray-400 hover:text-white">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('forgotPassword.backToSignIn')}
        </Link>
        <FloatingLanguageSelector />
      </div>

      <div className="max-w-md mx-auto space-y-8 mt-12">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">{t('forgotPassword.title')}</h1>
          <p className="text-gray-400">
            {t('forgotPassword.helpText')}
          </p>
        </div>

        {emailSent ? (
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-8 text-center space-y-6">
            <div className="flex justify-center">
              <CheckCircle2 className="w-16 h-16 text-green-400" />
            </div>
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">{t('forgotPassword.checkYourEmail')}</h2>
              <p className="text-gray-400">
                {t('forgotPassword.resetLinkSent')}
              </p>
              <p className="text-sm text-gray-500">
                {t('forgotPassword.noEmailReceived')}
              </p>
            </div>
            <div className="pt-4">
              <Link to="/coach-signin">
                <Button
                  className="bg-purple-600 text-white hover:bg-purple-700"
                >
                  {t('forgotPassword.backToSignIn')}
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6 space-y-6">
            {error && (
              <Alert variant="destructive" className="bg-red-900/20 border-red-800 text-red-300">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex items-center justify-center mb-4">
                <Mail className="w-12 h-12 text-purple-400" />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="email" className="block text-gray-300 font-medium">
                  {t('forgotPassword.emailLabel')}
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                  placeholder={t('forgotPassword.emailPlaceholder')}
                  disabled={loading}
                />
              </div>
              
              <Button
                type="submit"
                className="w-full bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? t('forgotPassword.sendingLink') : t('forgotPassword.sendResetLink')}
              </Button>
            </form>
          </div>
        )}

        <div className="text-center">
          <Link to="/coach-signin" className="text-gray-400 hover:text-white text-sm">
            {t('forgotPassword.rememberPassword')}
          </Link>
        </div>
      </div>
    </div>
  );
};
